// mvp-returns Phase E — TypeScript client matching the spec endpoints
// hosted at /ord/returns/* on de-order-service. Replaces the
// quarantined `apis/returns.ts` (C12-lite shape).

import { v4 as uuidv4 } from "uuid";
import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

// Every return state-mutation route wraps in `withStrictIdempotency`, which 400s
// (RET-002) without an Idempotency-Key header. A fresh uuid v4 per call gives each
// logical mutation its own key while letting genuine client retries replay safely.
const idem = (): Record<string, string> => ({ "Idempotency-Key": uuidv4() });

// Routes wrapped in `requireIfMatch` read the optimistic-lock version from the
// `If-Match` header (RET-090 without it). `!= null` so version 0 is sent, not dropped.
const ifMatch = (v?: number): Record<string, string> => (v != null ? { "If-Match": String(v) } : {});

// ── Enum-shaped union strings ──

export type ReturnRequestStatus =
  | "REQUESTED"
  | "PROPOSED"
  | "ACCEPTED"
  // Phase 0 R0.6 (§D11) — return-logistics states between accept and the gate
  | "AWAITING_PICKUP_SCHEDULE"
  | "AWAITING_SHIPMENT_INFO"
  | "IN_TRANSIT"
  // §D13 — carrier loss/damage/wrong-dest/stolen raised while IN_TRANSIT
  | "IN_TRANSIT_EXCEPTION"
  // Phase 0 R0.1 (§D10) — physical warehouse-receipt gate
  | "AWAITING_WAREHOUSE_RECEIPT"
  | "AWAITING_RESOLUTION"
  | "REFUND_PENDING"
  | "REJECTED"
  | "DISPUTED"
  | "COMPLETED"
  | "PARTIALLY_COMPLETED"
  | "CANCELLED";

export type RequestType = "SIMPLE" | "RETURN";
export type ReturnType = "FULL" | "PARTIAL";

export type ReturnReason =
  | "NOT_RECEIVED"
  | "DELIVERY_DAMAGED"
  | "WRONG_ITEM_DELIVERED"
  | "CANCEL_BEFORE_SHIP"
  | "DEFECTIVE"
  | "QUALITY_ISSUE"
  | "NOT_AS_DESCRIBED"
  | "CHANGED_MIND"
  | "EXPIRED"
  | "CONSIGNMENT_UNSOLD"
  | "WARRANTY_CLAIM"
  | "RECALL"
  | "PRICE_DISPUTE"
  | "OTHER";

export type ResolutionType =
  | "REFUND_MONEY"
  | "REPLACEMENT"
  | "NEXT_ORDER_DISCOUNT"
  | "NONE"
  | "MIXED";

export type RefundMethod =
  | "BANK_TRANSFER"
  | "INVOICE_DEDUCTION"
  | "CASH"
  | "CREDIT_BALANCE";

export type ShippingResponsibility =
  | "BUYER_DELIVERS"
  | "SELLER_PICKS_UP"
  | "BUYER_SHIPS"
  | "SELLER_PAYS_SHIPPING"
  | "NOT_REQUIRED";

export type InventoryImpactType =
  | "RESTOCK_GOOD"
  | "DAMAGED_WRITE_OFF"
  | "DAMAGED_SEPARATE_STOCK"
  | "EXPIRED_DESTROY";

// §D10 — physical condition assessed by the warehouse keeper on receipt.
export type ReceivedCondition =
  | "EXPECTED"
  | "WORSE_THAN_EXPECTED"
  | "BETTER_THAN_EXPECTED";

// ── Domain interfaces ──

export interface IReturnTimelineEntry {
  action: string;
  timestamp: string;
  userId?: string;
  notes?: string;
  // §9.2 — resolved actor display name (null when no userId / unresolved)
  actorName?: string | null;
}

// §9.2 Timeline (§5) — a future (○) step derived server-side from the return
// state machine. No timestamp (it has not happened yet).
export interface IReturnPendingStep {
  status: string;
  label: string;
}

// §9.1 trust badge tiers — mirrors BuyerTrustScore.level on de-order-service.
export type TrustTier = "EXCELLENT" | "GOOD" | "MODERATE" | "AT_RISK" | "HIGH_RISK";

// §9.1 derived sub-state hint rendered under the status badge. Computed
// server-side from `status` (see logics/return/list.ts SUB_STATUS_BY_STATUS).
export type ReturnSubStatus =
  | "BUYER_RESPONSE_PENDING"
  | "AWAIT_WAREHOUSE"
  | "AWAIT_PICKUP_SCHEDULE"
  | "AWAIT_SHIPMENT_INFO"
  | "IN_TRANSIT"
  | "AWAIT_RESOLUTION"
  | "REFUND_PENDING"
  | "DISPUTE_OPEN";

export interface IReturnSummary {
  returnRequestId: string;
  returnNumber: string;
  requestType: RequestType;
  status: ReturnRequestStatus;
  // §9.1 — humanised sub-state under the status badge (null when no hint).
  subStatus: ReturnSubStatus | null;
  orderNumber: string | null;
  buyerBusinessId: string | null;
  buyerBusinessName: string | null;
  // §T1c — business reference codes (BU-/SU-); null when unset.
  buyerBusinessRefCode: string | null;
  sellerBusinessName: string | null;
  sellerBusinessRefCode: string | null;
  returnReason: ReturnReason;
  // §9.1 — dominant line-item sub-reason ("Sub: Texture"); null when unset.
  subReason: string | null;
  resolutionType: ResolutionType | null;
  refundMethod: RefundMethod | null;
  isConsignmentReturn: boolean;
  isReceiveTimeRejection: boolean;
  totalRequestedAmount: number;
  refundAmount?: number;
  currency: string;
  version: number;
  requestedAt: string;
  completedAt: string | null;
  // §9.1 — running SLA clock for AWAIT_WAREHOUSE / pickup states ("2д өнгөр").
  // Both null when the status carries no SLA. slaDaysRemaining < 0 = overdue.
  slaDeadline: string | null;
  slaDaysRemaining: number | null;
  disputeCount: number;
  // §9.1 — dispute progress numerator (●●○); denominator is meta.disputeMaxRounds.
  disputeRound: number;
  disputeNotes: string | null;
  disputedAt: string | null;
  // §9 — manager escalation flag (powers the ESCALATED QUEUE)
  escalatedAt: string | null;
  // §9.1 — buyer trust badge. null when no trust row exists for this buyer.
  trustScore: number | null;
  trustTier: TrustTier | null;
  // Transit exception (BE contract phase 3 §3, O153): carried on every list row so the queue
  // needs no per-row GET. Dates are ISO strings.
  pickupCarrier?: string | null;
  pickupTrackingNumber?: string | null;
  inTransitSince?: string | null;
  transitExceptionType?: string | null;
  transitExceptionReportedBy?: string | null;
  transitExceptionReportedAt?: string | null;
  transitExceptionCarrierRef?: string | null;
  transitExceptionDescription?: string | null;
}

export interface IReturnItem {
  id: string;
  productId?: string | null;
  productName: string;
  variantName?: string | null;
  sku?: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  returnReason: ReturnReason;
  description?: string | null;
  evidencePhotos: string[];
  inventoryImpact?: InventoryImpactType | null;
  inventoryImpactSetAt?: string | null;
  inventoryImpactSetByUserId?: string | null;
  replacementOrderItemId?: string | null;
  // ── ★ MIXED RESOLUTION (Phase H19) ──
  itemResolutionType?: string | null;
  itemRefundMethod?: string | null;
  itemRefundAmount?: number | null;
  // ── §9.2 enrichment (added by get.ts; backward-compatible) ──
  // per-item sub-reason taxonomy + free note
  subReason?: string | null;
  subReasonNote?: string | null;
  // D15 / D10 — quantity history + warehouse receipt
  receivedQuantity?: number | null;
  receivedAt?: string | null;
  receiverUserId?: string | null;
  receivedCondition?: ReceivedCondition | null;
  bucketLocationCode?: string | null;
  itemStatus?: string | null;
  // mixed resolution replacement link
  itemReplacementOrderItemId?: string | null;
}

export interface IReturnDamagedInventory {
  id: string;
  returnRequestId?: string;
  // §T1a — source return number (list + get) and resolved names (nullable).
  returnNumber?: string | null;
  returnItemId: string;
  productId?: string | null;
  productName?: string | null;
  variantId?: string | null;
  sku?: string | null;
  inventoryImpact: InventoryImpactType;
  quantity: number;
  warehouseLocationId?: string | null;
  bucketLocationCode?: string | null;
  receivedAt: string;
  receivedByUserId: string;
  receivedByUserName?: string | null;
  damageDescription?: string | null;
  damageEvidencePhotos: string[];
  disposedAt?: string | null;
  disposedByUserId?: string | null;
  // §T1a — resolved disposer name (get only; null when unresolved).
  disposedByUserName?: string | null;
  disposalNotes?: string | null;
  finalDisposition?: string | null;
}

export interface IReturnBankAccount {
  bankName: string | null;
  number: string | null;
  name: string | null;
  currency: string | null;
}

export interface IReturnDetail {
  id: string;
  returnNumber: string;
  version: number;
  requestType: RequestType;
  status: ReturnRequestStatus;
  orderId: string;
  orderNumber: string | null;
  invoiceId: string | null;
  invoiceNumber: string | null;
  deliveryId: string | null;
  deliveryReceiptId: string | null;
  sourceDeliveryId: string | null;
  consignmentContractId: string | null;
  replacementOrderId: string | null;
  replacementDeliveryId: string | null;
  discountAppliedToOrderId: string | null;
  refundSettlementId: string | null;
  ebarimtReturnReceiptId: string | null;
  buyerBusinessId: string | null;
  sellerBusinessId: string | null;
  buyerBusinessName: string | null;
  sellerBusinessName: string | null;
  // §T1c — business reference codes (BU-/SU-); null when unset.
  buyerBusinessRefCode: string | null;
  sellerBusinessRefCode: string | null;
  buyerBankAccount: IReturnBankAccount | null;
  returnReason: ReturnReason;
  reasonDetails: string | null;
  evidencePhotos: string[];
  isReceiveTimeRejection: boolean;
  isConsignmentReturn: boolean;
  currency: string;
  totalRequestedAmount: number;
  refundPercent?: number;
  refundAmount?: number;
  deductionAmount?: number;
  deductionReason: string | null;
  resolutionType: ResolutionType | null;
  refundMethod: RefundMethod | null;
  discountCouponCode: string | null;
  refundConfirmedAt: string | null;
  refundConfirmedByUserId: string | null;
  // Section 20 — refund transaction details (set on confirm; shown when completed)
  refundTransferredAt: string | null;
  refundBankTransactionRef: string | null;
  refundBankReference: string | null;
  shippingResponsibility: ShippingResponsibility | null;
  shippingCost?: number;
  shippingNotes: string | null;
  proposalNotes: string | null;
  proposedAt: string | null;
  acceptedAt: string | null;
  rejectionReason: string | null;
  rejectedAt: string | null;
  disputeCount: number;
  disputedAt: string | null;
  // §18 / §9 — manager escalation flag
  escalatedAt: string | null;
  escalatedByUserId: string | null;
  escalationNote: string | null;
  creditNoteId: string | null;
  creditNoteNumber: string | null;
  requestedAt: string;
  completedAt: string | null;
  cancelledAt: string | null;

  // ── §9.2 enrichment (added by get.ts; backward-compatible) ──
  // dominant line-item sub-reason (derived; null when unset)
  subReason: string | null;
  // D2 — buyer refund preference / override
  preferredRefundMethod: RefundMethod | null;
  buyerOverrideAllowed: boolean;
  // D12 — target warehouse (id + resolved name)
  targetWarehouseId: string | null;
  targetWarehouseName: string | null;
  // D11 — pickup / transit
  pickupDeliveryNoteId: string | null;
  pickupTrackingNumber: string | null;
  pickupCarrier: string | null;
  pickupSlaDays: number;
  pickupScheduledAt: string | null;
  inTransitSince: string | null;
  // D13 — in-transit exception (Sections 16/17)
  transitExceptionType: string | null;            // LOST | DAMAGED_IN_TRANSIT | WRONG_DESTINATION | STOLEN
  transitExceptionReportedBy: string | null;      // CARRIER | MANAGER | BUYER
  transitExceptionReportedAt: string | null;
  transitExceptionCarrierRef: string | null;
  transitExceptionEvidencePhotos: string[] | null;
  transitExceptionDescription: string | null;
  transitExceptionResolution: string | null;      // SELLER_ABSORB | BUYER_ABSORB | INSURANCE_PAYOUT | CARRIER_LIABILITY
  transitExceptionInsuranceClaimId: string | null;
  transitExceptionResolutionReason: string | null;
  transitExceptionResolvedAt: string | null;
  transitExceptionResolvedByUserId: string | null;
  // D10 / SLA — warehouse receipt + derived clock (slaDaysRemaining < 0 = overdue)
  awaitingWarehouseReceiptSince: string | null;
  warehouseReceivedAt: string | null;
  warehouseReceivedByUserId: string | null;
  warehouseReceiverNotes: string | null;
  slaDeadline: string | null;
  slaDaysRemaining: number | null;
  // D1 — refund ETA preview
  estimatedRefundCompletionAt: string | null;
  estimatedRefundDays: number | null;
  estimateBasedOn: string | null;
  // D3 — off-system buyer claim + seller verification
  isOffSystemBuyerClaim: boolean;
  proofOfPurchase: string | null;
  proofOfPurchaseDate: string | null;
  sellerVerificationStatus: string | null;
  sellerVerifiedAt: string | null;
  sellerVerifiedByUserId: string | null;
  sellerVerificationNotes: string | null;
  // Actor ids + resolved names (de_user.user via name-resolver)
  createdByUserId: string | null;
  proposedByUserId: string | null;
  acceptedByUserId: string | null;
  rejectedByUserId: string | null;
  createdByName: string | null;
  proposedByName: string | null;
  acceptedByName: string | null;
  rejectedByName: string | null;
  // §9.2 Timeline (§5) — future (○) steps derived from the state machine
  pendingSteps: IReturnPendingStep[];
}

export interface IReturnGetResponse {
  returnRequest: IReturnDetail;
  items: IReturnItem[];
  damagedInventory: IReturnDamagedInventory[];
  timeline: IReturnTimelineEntry[];
}

export interface IReturnListParams {
  status?: ReturnRequestStatus | ReturnRequestStatus[];
  requestType?: RequestType;
  returnReason?: ReturnReason | ReturnReason[];
  resolutionType?: ResolutionType;
  orderId?: string;
  invoiceId?: string;
  deliveryId?: string;
  consignmentContractId?: string;
  buyerBusinessId?: string;
  sellerBusinessId?: string;
  isConsignmentReturn?: boolean;
  isReceiveTimeRejection?: boolean;
  returnNumberPrefix?: string;
  // O197 — BUYER = only returns I raised, SELLER = only returns addressed to me; omitted = both.
  side?: "BUYER" | "SELLER";
  // §9.1 — restrict to buyers at the given trust tier(s) (BuyerTrustScore.level).
  trustTier?: TrustTier | TrustTier[];
  // §9.1 — free-text search across returnNumber / orderNumber / buyer name / product name / SKU.
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  sortBy?: "requestedAt" | "createdAt" | "updatedAt" | "returnNumber" | "totalRequestedAmount";
  sortDir?: "asc" | "desc";
}

// §9.1 status sub-tab counts. `byStatus` is keyed by ReturnRequestStatus;
// missing keys mean zero. `total` = sum across statuses (the "Бүгд" badge).
// `receiveTimeRejection` backs the receive-time tab (status-agnostic).
export interface IReturnListCounts {
  total: number;
  byStatus: Partial<Record<ReturnRequestStatus, number>>;
  receiveTimeRejection: number;
}

// §9.1 page-level meta. disputeMaxRounds is the tenant dispute ceiling
// (●●○ denominator); warehouseReceiptSlaDays mirrors the server SLA window.
export interface IReturnListMeta {
  disputeMaxRounds: number;
  warehouseReceiptSlaDays: number;
}

export interface IReturnListResponse {
  returns: IReturnSummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  counts: IReturnListCounts;
  meta: IReturnListMeta;
}

// §9.2 Phase F — internal staff comment thread (§5). Attachments are
// already-uploaded file descriptors (the FE uploads via media-service and
// sends the resulting URLs).
export interface IReturnCommentAttachment {
  url: string;
  name: string;
  size?: number;
  type?: string;
}

export interface IReturnComment {
  id: string;
  body: string;
  authorUserId: string;
  authorName: string | null;
  authorRole: string;
  attachments: IReturnCommentAttachment[];
  createdAt: string;
}

// ── Endpoints ──

export const list = (params?: IReturnListParams) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/ord/returns", params as any) as Promise<IReturnListResponse>;

export const get = (id: string) =>
  httpRequest.get(`/ord/returns/${id}`) as Promise<IReturnGetResponse>;

// §10 dispute — category dictionary + suggested phrases for the buyer dispute
// modal. GET /ord/returns/dispute-templates (gated by the caller's business
// scope server-side; minMessageLength is the dispute textarea minimum).
export interface IDisputeTemplateCategory {
  code: string;
  // B68: FE i18n key ("returns.disputeCategory.<CODE>"); `label` is the Mongolian fallback.
  labelKey?: string;
  label: string;
  suggestions: string[];
}

export interface IDisputeTemplatesResponse {
  minMessageLength: number;
  categories: IDisputeTemplateCategory[];
}

export const getDisputeTemplates = () =>
  httpRequest.get("/ord/returns/dispute-templates") as Promise<IDisputeTemplatesResponse>;

// ───────────────────────────────────────────────────────────────────────────
// BUYER returns surface (mvp-returns Phase 2 — screens-buyer-app §1, §2).
// These reuse the role-agnostic routes above (the backend scopes every row to
// the caller's currentBusinessId). Phase 3 (wizard) and Phase 4 (detail)
// conform to the names + signatures declared here.
// ───────────────────────────────────────────────────────────────────────────

// §2 hub — alias over `list`; the buyer business scope is applied server-side
// (route injects myBusinessId = req.user.currentBusinessId). Returns the same
// IReturnListResponse (returns[], pagination, counts.byStatus, meta).
// O197: side=BUYER limits the rows to returns the caller's business raised.
export const listMyReturns = (params?: IReturnListParams) => list({ ...params, side: "BUYER" });

// §1 Path-A entry — per-order return aggregate (history + per-line returned qty).
// GET /ord/returns?orderId=:id&aggregateOnly=true. orderId is REQUIRED (the route
// 400s without it). byStatus/total count EVERY return on the order; perItem
// counts non-terminal returns only (CANCELLED/REJECTED free the quantity).
export interface IOrderReturnAggregatePerItem {
  orderItemId: string;
  returnedQuantity: number;
  returnNumbers: string[];
}

export interface IOrderReturnAggregate {
  orderId: string;
  byStatus: Partial<Record<ReturnRequestStatus, number>>;
  total: number;
  perItem: IOrderReturnAggregatePerItem[];
}

export const getOrderReturnAggregate = (orderId: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/ord/returns", { orderId, aggregateOnly: true } as any) as Promise<IOrderReturnAggregate>;

// §1 / §3 Step-0 — order-level return-window fields (added by Phase 1 to both
// order detail GET /ord/order/:id and the buyer order list POST /ord/order/list).
export interface IReturnWindowFields {
  deliveredAt: string | null;
  returnWindowDays: number;
  returnWindowExpiresAt: string | null;
  returnWindowDaysRemaining: number | null;
  isReturnEligible: boolean;
}

// §1 — a single order line on the buyer order detail, augmented by Phase 1 with
// the per-line return aggregation. Only the fields the buyer return surface reads
// are typed; the underlying order line carries many more (kept loose via index).
export interface IReturnableOrderLine {
  id: string;                       // order line PK — the create `orderItemId` join key
  name?: string | null;
  skuCode?: string | null;
  unit?: string | null;
  image?: string | null;
  quantity: number;
  price: number;
  // Phase 1 enrichment
  alreadyReturnedQuantity: number;
  availableReturnQuantity: number;
  returnNumbers: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// §1 — the buyer order detail shape the return surface consumes. The order GET
// returns far more (payment, branches, attachments); this narrows to the parts
// Path-A + the wizard need. Loose index keeps the rest accessible.
export interface IReturnOrderDetail extends IReturnWindowFields {
  id: string;
  purchaseCode?: string | null;
  salesCode?: string | null;
  orderNumber?: string | null;
  orderStatus: string;
  totalAmount?: number;
  currency?: string | null;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  deliveryNoteId?: string | null;
  // counterparties (buyer order: receiverBusiness = supplier/seller)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  receiverBusiness?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  senderBusiness?: any;
  lines: IReturnableOrderLine[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// §1 / §4 — buyer order detail with per-item availableReturnQuantity + window.
// GET /ord/order/:id (Phase-1 enriched). Same call OrderApi.get uses; typed here
// so the return surface gets the enrichment fields without `any`.
export const getOrderForReturn = (orderId: string) =>
  httpRequest.get(`/ord/order/${orderId}`) as Promise<IReturnOrderDetail>;

export interface IDeliveredOrdersParams {
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  orderStatus?: string;
  deliveryNoteStatus?: string;
  returnableOnly?: boolean;
  page?: number;
  limit?: number;
}

export interface IDeliveredOrdersResponse {
  // rows are full order.toJSON() + the window fields; narrowed to IReturnOrderDetail-ish
  rows: IReturnOrderDetail[];
  count?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const getDeliveredOrders = (params?: IDeliveredOrdersParams) => {
  const { search, dateFrom, dateTo, orderStatus, deliveryNoteStatus, returnableOnly, page = 1, limit = 10 } = params ?? {};
  const body = {
    filter: {
      excel: false,
      ...(orderStatus ? { orderStatus } : {}),
      ...(deliveryNoteStatus ? { deliveryNoteStatus } : {}),
      ...(returnableOnly ? { returnableOnly: true } : {}),
      ...(search ? { search } : {}),
      ...(dateFrom ? { startDate: dateFrom } : {}),
      ...(dateTo ? { endDate: dateTo } : {}),
    },
    offset: { page, limit },
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return httpRequest.post("/ord/order/list", body as any) as Promise<IDeliveredOrdersResponse>;
};

export const getReturnableOrders = async (
  params?: Omit<IDeliveredOrdersParams, "orderStatus" | "deliveryNoteStatus" | "returnableOnly" | "page" | "limit">,
): Promise<IDeliveredOrdersResponse> => {
  const res = await getDeliveredOrders({ ...params, deliveryNoteStatus: "DELIVERED", returnableOnly: true, page: 1, limit: 50 });
  const rows = (res.rows ?? [])
    .slice()
    .sort((a, b) => (b.deliveredAt ?? "").localeCompare(a.deliveredAt ?? ""));
  return { rows, count: res.count ?? rows.length };
};

// §4-§7 wizard — create a return request. POST /ord/returns (Idempotency-Key via
// idem()). The server injects myBusinessId / tenantId / userId from req.user — do
// NOT send them. requestType=RETURN requires >=1 request-level evidencePhoto.
// items[].orderItemId === order line.id (the Phase-1 join key).
export interface ICreateReturnItem {
  orderItemId: string;             // order line PK (line.id)
  quantity: number;                // integer >= 1, <= availableReturnQuantity
  returnReason: ReturnReason;
  description?: string | null;
  evidencePhotos?: string[];       // max 5 per item
  unitPrice: number;
  productId?: string | null;
  productName: string;
  variantName?: string | null;
  sku?: string | null;
}

export interface ICreateReturnBody {
  requestType: RequestType;        // "RETURN" (delivered) | "SIMPLE"
  orderId: string;
  invoiceId?: string | null;
  deliveryId?: string | null;
  deliveryReceiptId?: string | null;
  isReceiveTimeRejection?: boolean;
  sourceDeliveryId?: string | null;
  isConsignmentReturn?: boolean;
  consignmentContractId?: string | null;
  returnType: ReturnType;          // "FULL" | "PARTIAL"
  returnReason: ReturnReason;
  reasonDetails?: string | null;
  items: ICreateReturnItem[];
  evidencePhotos?: string[];       // max 10; >=1 required when requestType=RETURN
  // §D2 — buyer refund preference (optional hint; staff decides final).
  preferredRefundMethod?: RefundMethod | null;
  // §D11 — buyer transport choice.
  shippingResponsibility?: ShippingResponsibility | null;
  sellerBusinessId: string;
  sellerBusinessName?: string | null;
  buyerBusinessName?: string | null;
  orderNumber?: string | null;
  invoiceNumber?: string | null;
}

export interface ICreateReturnResponse {
  returnRequestId?: string;
  returnNumber?: string;
  status?: ReturnRequestStatus;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const createReturn = (body: ICreateReturnBody) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post("/ord/returns", body as any, idem()) as Promise<ICreateReturnResponse>;

// §D1 — refund ETA preview. Pure compute POST: the route has NO idempotency /
// If-Match wrapper, so we send a plain body with no idem()/ifMatch() headers.
export interface IRefundTimelinePreview {
  refundMethod: RefundMethod | string;
  estimatedRefundDays: number;
  // B67: lower bound of the tenant min..max range (BANK_TRANSFER); null when the
  // method has a single value.
  estimatedRefundDaysMin?: number | null;
  estimatedCompletionAt: string;
  basedOn: string;
}

export interface IPreviewRefundTimelineResponse {
  requestType: string;
  requestedAt: string;
  previews: IRefundTimelinePreview[];
}

export const previewRefundTimeline = (body: {
  requestType: string;
  refundMethod?: RefundMethod | string | Array<RefundMethod | string>;
  requestedAt?: string;
  // B67: the order's seller — whose tenant refund SLA applies (BE optional UUID).
  sellerBusinessId?: string;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post("/ord/returns/preview-refund-timeline", body as any) as Promise<IPreviewRefundTimelineResponse>;

// §9.2 §5 — audit-log PDF export → media-service file URL. (The JSON export
// is built FE-side from the detail data; only the PDF is server-side.)
export const auditPdf = (id: string) =>
  httpRequest.get(`/ord/returns/${id}/audit-pdf`) as Promise<{ url: string }>;

// §9.2 Phase F — comment thread (read oldest-first; POST returns the created
// comment with resolved authorName). attachments = already-uploaded URLs.
export const listComments = (id: string) =>
  httpRequest.get(`/ord/returns/${id}/comments`) as Promise<{ comments: IReturnComment[] }>;

export const addComment = (id: string, body: { body: string; attachments?: IReturnCommentAttachment[] }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/comments`, body as any, idem()) as Promise<IReturnComment>;

export const propose = (id: string, body: {
  refundPercent: number;
  deductionReason?: string | null;
  shippingResponsibility: ShippingResponsibility;
  shippingCost?: number | null;
  shippingNotes?: string | null;
  proposalNotes?: string | null;
  resolutionType: ResolutionType;
  refundMethod?: RefundMethod | null;
  // §T2a/§T2b — optional propose-time overrides. pickupSlaDays only when changed;
  // targetWarehouseChangeReason required (R-213) when targetWarehouseId differs.
  pickupSlaDays?: number;
  targetWarehouseId?: string | null;
  targetWarehouseChangeReason?: string | null;
  expectedVersion?: number;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/propose`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

// §T2b — seller warehouses for the propose-time target-warehouse picker.
// GET /ord/returns/warehouses (gated RETURN_PROPOSE). code = warehouse refCode.
export interface IReturnWarehouse {
  id: string;
  name: string;
  code: string | null;
}

export const listWarehouses = () =>
  httpRequest.get("/ord/returns/warehouses") as Promise<{ warehouses: IReturnWarehouse[] }>;

export const accept = (id: string, body: { buyerNotes?: string | null; expectedVersion?: number }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/accept`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

export const reject = (id: string, body: { rejectionReason: string; expectedVersion?: number }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/reject`, body as any, idem());

export const dispute = (id: string, body: { disputeNotes: string; expectedVersion?: number }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/dispute`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

// §9 — generic "Manager-руу" escalation; sets the escalation flag (timeline +
// escalatedAt). Idempotency-Key + If-Match.
export const escalate = (id: string, body: { reason: string; expectedVersion?: number }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/escalate`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

// §18 — manager dispute override (DISPUTED only). RESET_DISPUTE / FORCE_REJECTED
// / FORCE_ACCEPTED / ESCALATE_SENIOR; note required (min 3). Idempotency + If-Match.
export type DisputeOverrideAction = "RESET_DISPUTE" | "FORCE_REJECTED" | "FORCE_ACCEPTED" | "ESCALATE_SENIOR";

export const escalateDispute = (id: string, body: {
  action: DisputeOverrideAction;
  note: string;
  expectedVersion?: number;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/escalate-dispute`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

export const cancel = (id: string, body: { reason?: string | null; expectedVersion?: number }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/cancel`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

export const setResolution = (id: string, body: {
  resolutionType: ResolutionType;
  refundMethod?: RefundMethod | null;
  reason: string;
  managerOverride?: boolean;
  expectedVersion?: number;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/set-resolution`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

export const confirmRefund = (id: string, body: {
  bankTransactionRef: string;
  // Section 20 — "Шилжүүлсэн огноо" is required (ISO string).
  transferredAt: string;
  bankReference?: string | null;
  transferScreenshotUrl?: string | null;
  notes?: string | null;
  expectedVersion?: number;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/confirm-refund`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

export const setInventoryImpact = (id: string, itemId: string, body: {
  allocations: Array<{
    inventoryImpact: InventoryImpactType;
    quantity: number;
    warehouseLocationId?: string | null;
    bucketLocationCode?: string | null;
    damageDescription?: string | null;
    damageEvidencePhotos?: string[];
  }>;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/items/${itemId}/inventory-impact`, body as any, idem());

// ── Phase H19 — per-item mixed resolution ──

export interface ISetItemResolutionBody {
  // MIXED is not allowed at item level — the parent carries MIXED.
  itemResolutionType?: Exclude<ResolutionType, "MIXED"> | null;
  itemRefundMethod?: RefundMethod | null;
  itemRefundAmount?: number | null;
}

export interface ISetItemResolutionResponse {
  returnItemId: string;
  itemResolutionType: string | null;
  itemRefundMethod: string | null;
  itemRefundAmount: number | null;
}

export const setItemResolution = (returnRequestId: string, returnItemId: string, body: ISetItemResolutionBody) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${returnRequestId}/items/${returnItemId}/set-resolution`, body as any, idem()) as Promise<ISetItemResolutionResponse>;

// ── Replacement feasibility ──

export interface IReplacementFeasibilityItem {
  returnItemId: string;
  productId: string | null;
  productName: string;
  sku: string | null;
  requestedQuantity: number;
  availableQuantity: number;
  sufficient: boolean;
  shortageQuantity: number;
}

export interface IReplacementFeasibilityResponse {
  returnRequestId: string;
  sellerBusinessId: string;
  allFeasible: boolean;
  partiallyFeasible: boolean;
  suggestion: "OK_TO_REPLACE_FULL" | "CONSIDER_MIXED_RESOLUTION" | "FALLBACK_TO_REFUND_MONEY";
  items: IReplacementFeasibilityItem[];
}

export const replacementFeasibility = (id: string) =>
  httpRequest.get(`/ord/returns/${id}/replacement-feasibility`) as Promise<IReplacementFeasibilityResponse>;

// ── Bulk action ──

export type BulkAction = "PROPOSE" | "REJECT" | "CANCEL" | "ACCEPT" | "CONFIRM_REFUND";

export interface IBulkActionProposeBody {
  refundPercent: number;
  deductionReason?: string | null;
  shippingResponsibility: ShippingResponsibility;
  shippingCost?: number | null;
  shippingNotes?: string | null;
  proposalNotes?: string | null;
  resolutionType: ResolutionType;
  refundMethod?: RefundMethod | null;
}

export interface IBulkActionRejectBody {
  rejectionReason: string;
}

export interface IBulkActionCancelBody {
  reason?: string | null;
}

export interface IBulkActionAcceptBody {
  buyerNotes?: string | null;
}

export interface IBulkActionConfirmRefundBody {
  bankTransactionRef: string;
}

export interface IBulkActionInput {
  action: BulkAction;
  returnRequestIds: string[];
  proposeBody?: IBulkActionProposeBody;
  rejectBody?: IBulkActionRejectBody;
  cancelBody?: IBulkActionCancelBody;
  acceptBody?: IBulkActionAcceptBody;
  confirmRefundBody?: IBulkActionConfirmRefundBody;
}

export interface IBulkActionResultItem {
  returnRequestId: string;
  ok: boolean;
  status?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface IBulkActionResponse {
  action: BulkAction;
  total: number;
  succeeded: number;
  failed: number;
  results: IBulkActionResultItem[];
}

export const bulkAction = (body: IBulkActionInput): Promise<IBulkActionResponse> =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post("/ord/returns/bulk-action", body as any, idem()) as Promise<IBulkActionResponse>;

// ── Phase 0 R0.1b (§D10) — warehouse-receipt gate ──
// POST /ord/returns/:id/confirm-warehouse-receipt. The server injects
// myBusinessId / userId / returnRequestId; the UI sends only the fields below.

export interface IConfirmWarehouseReceiptItem {
  returnItemId: string;
  actualQuantity: number;              // integer >= 1, must not exceed accepted qty
  inventoryImpact: InventoryImpactType;
  bucketLocationCode?: string | null;  // required for RESTOCK_GOOD / DAMAGED_SEPARATE_STOCK
  receivedCondition: ReceivedCondition;
  photoUrls?: string[];                // max 10; >=1 required when WORSE_THAN_EXPECTED
  notes?: string | null;
}

export interface IConfirmWarehouseReceiptBody {
  warehouseId: string;                 // the return's target warehouse
  receivedAt?: string;                 // ISO; defaults to now server-side
  receiverNotes?: string | null;
  expectedVersion?: number;            // optimistic version from the return detail
  items: IConfirmWarehouseReceiptItem[];
}

// A shelf bucket was chosen but the units never reached the shelf. The receipt
// itself still stands; the server parks each issue on the failed-operations
// queue (Буцаалт → Тохиргоо → Амжилтгүй үйлдлүүд) and the retry tick re-posts
// what it can.
export interface IStockIssue {
  reason: "NO_VARIANT_LINK" | "POSTING_FAILED";
  returnItemId: string | null;
  sku: string | null;
  quantity: number;
  detail: string;
}

export interface IConfirmWarehouseReceiptResponse {
  returnRequestId: string;
  status: ReturnRequestStatus;
  receivedAt: string;
  inventoryAdjustments: Array<{ variantId: string; quantity: number; movementType: string; inventoryImpact: string }>;
  // false when a shelf bucket was chosen and the stock post did not land.
  inventoryPosted: boolean;
  stockIssues: IStockIssue[];
  exceptions: Array<{ type: string; expected: number; actual: number; note?: string }>;
}

// Sends a fresh Idempotency-Key like every other return mutation. expectedVersion
// travels in the body — this route reads it from req.body (no If-Match required).
export const confirmWarehouseReceipt = (id: string, body: IConfirmWarehouseReceiptBody) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/confirm-warehouse-receipt`, body as any, idem()) as Promise<IConfirmWarehouseReceiptResponse>;

// ── §D11 pickup / transit (Тээвэр tab) ──
// All three POST routes wrap withStrictIdempotency (need Idempotency-Key) and
// read expectedVersion from the body (no If-Match). Mirrors the pattern above.

export interface IPickupActionResponse {
  returnRequestId: string;
  status: ReturnRequestStatus;
  pickupScheduledAt?: string | null;
  inTransitSince?: string | null;
  pickupCarrier?: string | null;
  pickupTrackingNumber?: string | null;
}

// AWAITING_PICKUP_SCHEDULE → IN_TRANSIT (SELLER_PICKS_UP). expectedVersion in body.
export const schedulePickup = (id: string, body: {
  pickupScheduledAt?: string;
  pickupDeliveryNoteId?: string | null;
  notes?: string | null;
  expectedVersion?: number;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/schedule-pickup`, body as any, idem()) as Promise<IPickupActionResponse>;

// AWAITING_SHIPMENT_INFO → IN_TRANSIT (BUYER_SHIPS). carrier + tracking required.
export const markShipped = (id: string, body: {
  trackingNumber: string;
  carrier: string;
  expectedVersion?: number;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/mark-shipped`, body as any, idem()) as Promise<IPickupActionResponse>;

// IN_TRANSIT → AWAITING_WAREHOUSE_RECEIPT. Seller warehouse staff only
// (RETURN_WAREHOUSE_RECEIVE). Idempotent on the backend; sends an Idempotency-Key.
export const markArrived = (id: string, body: { note?: string | null; expectedVersion?: number }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/arrived`, body as any, idem()) as Promise<IPickupActionResponse>;

// AWAITING_RESOLUTION → COMPLETED for a consignment return. Seller only
// (RETURN_WAREHOUSE_RECEIVE). Idempotent on the backend; sends an Idempotency-Key.
export const resolveConsignment = (id: string, body: { note?: string | null; expectedVersion?: number }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/resolve-consignment`, body as any, idem()) as Promise<IPickupActionResponse>;

// IN_TRANSIT — update carrier/tracking. tracking required, carrier optional.
export const updateTracking = (id: string, body: {
  trackingNumber: string;
  carrier?: string | null;
  expectedVersion?: number;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/update-tracking`, body as any, idem()) as Promise<IPickupActionResponse>;

// ── §D13 in-transit exception (Sections 16/17) ──
// Both routes require an Idempotency-Key + If-Match=version (mirror confirmRefund).

export type TransitExceptionType = "LOST" | "DAMAGED_IN_TRANSIT" | "WRONG_DESTINATION" | "STOLEN";
export type TransitExceptionReportedBy = "CARRIER" | "MANAGER" | "BUYER";
export type TransitExceptionResolution =
  | "SELLER_ABSORB" | "BUYER_ABSORB" | "INSURANCE_PAYOUT" | "CARRIER_LIABILITY";

// IN_TRANSIT → IN_TRANSIT_EXCEPTION. Manual/manager report path.
export const reportTransitException = (id: string, body: {
  exceptionType: TransitExceptionType;
  reportedBy: TransitExceptionReportedBy;
  carrierRef?: string | null;
  evidencePhotos?: string[];
  description?: string | null;
  expectedVersion?: number;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/transit-exception`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

// IN_TRANSIT_EXCEPTION → AWAITING_RESOLUTION (SELLER_ABSORB/INSURANCE_PAYOUT/
// CARRIER_LIABILITY) or REJECTED (BUYER_ABSORB). Manager-gated (RET-061).
// insuranceClaimId required iff resolution=INSURANCE_PAYOUT; refundPercent optional
// override (BUYER_ABSORB forces 0%, others default 100%); auditReason required.
export const resolveTransitException = (id: string, body: {
  resolution: TransitExceptionResolution;
  insuranceClaimId?: string | null;
  refundPercent?: number;
  auditReason: string;
  expectedVersion?: number;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/resolve-transit-exception`, body as any, { ...idem(), ...ifMatch(body.expectedVersion) });

// ── §D3 — off-system buyer claim seller verification (single-step) ──
// POST /ord/returns/:id/verify-seller wraps `withStrictIdempotency` (needs an
// Idempotency-Key) and does NOT use requireIfMatch, so no If-Match/version goes
// up — the route asserts RETURN_OFFSYSTEM_VERIFY and spreads { decision, notes }.
// Valid only while isOffSystemBuyerClaim && sellerVerificationStatus === "PENDING".
//   VERIFIED → sellerVerificationStatus = VERIFIED; the return then continues the
//              standard propose/accept flow (no separate manager-approval step).
//   REJECTED → status becomes REJECTED (terminal); `notes` is stored as rejectionReason.
export type SellerVerificationDecision = "VERIFIED" | "REJECTED";

export const verifySeller = (id: string, body: {
  decision: SellerVerificationDecision;
  notes?: string | null;
}) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/returns/${id}/verify-seller`, body as any, idem());
